import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { sharedSidebarFetch } from "../../../core/data/json/horizontalSidebar";
import CrmsModal from "../../../core/modals/crms_modal";
import DropdownSelector from "./DropdownSelector";
import ObservationCell from "./ObservationCell";
import { ResponsiveLeadsList } from "./ResponsiveLeadsList";
import EditableNameCell from "./EditableNameCell";
import CardViewModal from "./CardViewModal";
import { API_URL } from '../../../environment';
import { getSupabaseRealtimeCRM } from "../../../supabaseRealtime";
import KanbanDisplay from "./KanbanDisplay";
import { Modal } from "react-bootstrap";
import MiniChat from "./MiniChat";


interface LeadData {
  key: number;
  LeadName: string;
  Phone: string;
  FullPhone?: string;
  Tags: string;
  Funil: string;
  Data: string;
  Cnpj: string;
  Razao: string;
  Obs: string;
  etapaCadencia?: number | null;
  qtdMsgTexto?: number;
  IdUserAtual?: number | null;
  IdTime?: number | null;
}

interface Stage {
  id: number;
  nome_funil: string;
  funil?: string;
  ordem?: number;
  atendimento_humano?: string;
  closer?: string;
}


const LeadsList: React.FC = () => {
  const routes = all_routes;
  const isDark = useSelector((state: any) => state.themeSetting.dataTheme) === "dark";
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  const [stages, setStages] = useState<Stage[]>([]);
  const [funnels, setFunnels] = useState<Stage[]>([]);
  const [nomePai, setNomePai] = useState<string>("Leads");
  const isFetchingStages = useRef(false);
  const isFetchingFunnels = useRef(false);
  const [tableKey, setTableKey] = useState(0);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [data, setData] = useState<LeadData[]>([]);
  // Espelho do `data` em ref — usado para resolver o id_time do lead dentro das
  // closures de mover/transferir (admin master vê vários times de uma vez, então
  // a operação precisa do time do PRÓPRIO lead, não de um time global).
  const dataRef = useRef<LeadData[]>([]);
  useEffect(() => { dataRef.current = data; }, [data]);
  // Realtime de leads (funil_fluxo, banco CRM): refs p/ o handler estável da
  // subscription ler `stages` e disparar o fetch sempre na versão atual.
  const stagesRef = useRef<Stage[]>([]);
  const fetchTableDataRef = useRef<() => void>(() => {});
  const realtimeLeadsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { stagesRef.current = stages; }, [stages]);
  const idTimeDoLead = (fone: string): string => {
    const lead = dataRef.current.find(l => l.FullPhone === fone || l.Phone === fone);
    return lead?.IdTime != null ? String(lead.IdTime) : selectedTimeId;
  };
  const [updating, setUpdating] = useState<boolean>(false);
  const moveQueue = useRef<{ fone: string; novaEtapa: string; sourceEtapa: string }[]>([]);
  const isProcessingMove = useRef(false);
  const pendingMoves = useRef(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  // (não há mais `totalLeads`: o total da paginação sai de filteredAndSortedData.
  //  A paginação é local — /pegarclientes devolve a lista inteira, sem LIMIT — e o
  //  estado antigo nunca era preenchido, deixando o rodapé em "0" e prendendo a
  //  tela na primeira página.)
  const [selectedFunnel, setSelectedFunnel] = useState<string>("Todos");
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const isFetchingTable = useRef(false);
  const isFetchingGroup = useRef(false);
  const isMounted = useRef(true);
  const tabsRef = useRef<HTMLDivElement>(null);

  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = 200;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const empty = data.length === 0;
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedChatContact, setSelectedChatContact] = useState<{phone: string, name: string} | null>(null);
  // Mobile: lead aberto na tela de detalhe (cards compactos → detalhe editável)
  const [mobileLeadDetail, setMobileLeadDetail] = useState<LeadData | null>(null);
  const [mobileObsDraft, setMobileObsDraft] = useState<string>("");
  const [mobileObsSaving, setMobileObsSaving] = useState<boolean>(false);

  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
  const [times, setTimes] = useState<{ id: string; nome: string }[]>([]);
  const [selectedTimeId, setSelectedTimeId] = useState<string>(
    localStorage.getItem("id_cliente_filter") || localStorage.getItem("id_cliente") || ""
  );
  
  // ── Filtro de TIME (admin master) ──────────────────────────────────────────
  // O admin master recebe os leads de TODOS os times do grupo numa só chamada,
  // mas as abas de funil vinham só do time do login: um funil que existe apenas
  // em outro time não aparecia como opção, e seus leads sumiam ao filtrar.
  // "todos" = sem filtro de time.
  const [filtroTime, setFiltroTime] = useState<string>("todos");
  // Funil selecionado no modo admin master. Chave "{id_time}|{funil}" porque o
  // mesmo nome de funil ("LEADS") existe em times diferentes — filtrar só pelo
  // nome misturaria times. "todos" = sem filtro de funil.
  const [funilKey, setFunilKey] = useState<string>("todos");
  // Nome do time pelo id (lista `times` do grupo). Declarado aqui em cima porque
  // os memos de filtro logo abaixo já dependem dele.
  const nomeDoTime = (id?: number | null | string) =>
    id != null ? (times.find((t) => String(t.id) === String(id))?.nome || `Time ${id}`) : "";

  const [usuarios, setUsuarios] = useState<{ id: number; nome: string; id_time?: number }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>(
    localStorage.getItem("id_user_filter") || 
    ((!isAdmin && !isAdminMaster) ? (localStorage.getItem("id_user") || "") : "")
  );

  const [cardModalShow, setCardModalShow] = useState(false);
  const [selectedLeadPhone, setSelectedLeadPhone] = useState("");
  const [selectedLeadName, setSelectedLeadName] = useState("");
  const [selectedFunnelName, setSelectedFunnelName] = useState("");

  const handleOpenCardModal = (lead: LeadData) => {
    setSelectedLeadPhone(lead.FullPhone || lead.Phone);
    setSelectedLeadName(lead.LeadName);
    setSelectedFunnelName(localStorage.getItem("nome_pai") || "");
    setCardModalShow(true);
  };

  // Processa a fila de movimentos sequencialmente
  const processMoveQueue = useCallback(async () => {
    if (isProcessingMove.current || moveQueue.current.length === 0) return;
    isProcessingMove.current = true;

    const { fone, novaEtapa } = moveQueue.current[0];

    try {
      const res = await fetch(`${API_URL}/atualizaretapa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone, nova_etapa: novaEtapa, idcliente: idTimeDoLead(fone) }),
      });
      if (!res.ok) throw new Error("Falha ao mover");
    } catch (err) {
      console.error("Erro ao mover lead:", err);
    } finally {
      moveQueue.current.shift();
      pendingMoves.current--;
      isProcessingMove.current = false;

      if (moveQueue.current.length > 0) {
        processMoveQueue();
      } else {
        // Todas as requisições terminaram — aguarda o servidor commitar e sincroniza
        setTimeout(() => {
          fetchTableData().then(() => setUpdating(false));
        }, 800);
      }
    }
  }, [selectedTimeId]);

  const handleMoveLead = useCallback((fone: string, novaEtapa: string, sourceEtapa: string) => {
    // Admin master vê times diferentes no mesmo quadro: /atualizaretapa resolve a
    // etapa destino pelo NOME dentro de (id_time, id_pai) do próprio lead. Soltar
    // num nome que não existe no funil dele estouraria a rota — barra antes.
    const lead = dataRef.current.find((l) => l.FullPhone === fone || l.Phone === fone);
    if (isAdminMaster && lead) {
      const doTime = etapasPorTimeRef.current[String(lead.IdTime)];
      const existe = doTime?.some(
        (s) => s.nome_funil === novaEtapa && (s.funil || "Sem Funil") === (lead.Funil || "Sem Funil")
      );
      if (doTime && !existe) {
        alert(`A etapa "${novaEtapa}" não existe no funil "${lead.Funil || "Sem Funil"}" do time ${nomeDoTime(lead.IdTime)}.`);
        return;
      }
    }

    // Trava o polling imediatamente
    setUpdating(true);
    pendingMoves.current++;

    // Atualização otimista: move visualmente na hora
    setData(prev => prev.map(l => (l.FullPhone === fone || l.Phone === fone) ? { ...l, Tags: novaEtapa } : l));

    // Enfileira a requisição
    moveQueue.current.push({ fone, novaEtapa, sourceEtapa });
    processMoveQueue();
    // `times` nas deps: o aviso de etapa inexistente cita o nome do time.
  }, [processMoveQueue, times]);

  const openMiniChat = (phone: string, name: string) => {
    setSelectedChatContact({ phone, name });
    setShowChatModal(true);
  };

  useEffect(() => {
    if (isAdminMaster) {
      const id_grupo = localStorage.getItem("id_grupo");
      if (id_grupo) {
        fetch(`${API_URL}/pegartodostimes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_grupo }),
        }).then(res => res.json()).then(data => { if (Array.isArray(data)) setTimes(data); });
      }
    }
  }, [isAdminMaster]);

  useEffect(() => {
    if (!selectedTimeId) { setUsuarios([]); return; }
    // Admin master vê leads de TODOS os times → precisa dos atendentes de todos os
    // times do grupo para resolver a coluna ATENDENTE (senão leads de outros times
    // ficam "sem atendente"). Demais perfis usam só os usuários do próprio time.
    const id_grupo = localStorage.getItem("id_grupo");
    const req = (isAdminMaster && id_grupo)
      ? fetch(`${API_URL}/pegarusuariosgrupo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_grupo }),
        })
      : fetch(`${API_URL}/pegarusuariostime`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_time: selectedTimeId }),
        });
    req.then(res => res.json()).then(data => { if (Array.isArray(data)) setUsuarios(data); else setUsuarios([]); });
  }, [selectedTimeId]);

  const handleTableChange = (newPagination: any, filters?: any) => {
    setCurrentPage(newPagination.current);
    setPageSize(newPagination.pageSize);
    if (filters && Object.prototype.hasOwnProperty.call(filters, "Tags")) {
      const next = Array.isArray(filters.Tags) ? (filters.Tags as string[]) : [];
      setSelectedStages(next);
    }
    // Coluna TIME e o seletor do topo são o MESMO filtro — mantê-los no mesmo
    // estado evita a tela mostrar um time no seletor e outro na coluna.
    if (filters && Object.prototype.hasOwnProperty.call(filters, "IdTime")) {
      const escolhidos = Array.isArray(filters.IdTime) ? (filters.IdTime as string[]) : [];
      setFiltroTime(escolhidos.length === 1 ? String(escolhidos[0]) : "todos");
    }
  };

  useEffect(() => {
    if (selectedTimeId) {
      hasLoadedOnce.current = false;
      setLoading(true);
      fetchStages();
      fetchFunnels();
      fetchTableData();
      fetchGroup();
    }
  }, [selectedTimeId, selectedUserId, currentPage, pageSize, selectedFunnel]);

  // Admin master: a busca agregada depende da lista de times (carregada async).
  // Quando ela chega, dispara a leads-list de todos os times.
  useEffect(() => {
    if (isAdminMaster && times.length > 0) {
      hasLoadedOnce.current = false;
      fetchTableData();
    }
  }, [times]);

  // Leads que passam SÓ pelo filtro de time — base das abas de funil e das
  // opções de etapa, para que elas ofereçam exatamente o que existe na tela.
  const leadsDoTimeFiltrado = React.useMemo(
    () => (filtroTime === "todos" ? data : data.filter((l) => String(l.IdTime) === filtroTime)),
    [data, filtroTime]
  );

  // Abas de funil do admin master: derivadas dos leads carregados (os funis são
  // por time, e /pegarpaginas só conhece o time do login). O rótulo só ganha o
  // nome do time quando o mesmo funil existe em mais de um time — sem poluir
  // quando não há ambiguidade.
  const funisDisponiveis = React.useMemo(() => {
    const porChave = new Map<string, { key: string; funil: string; idTime: string; total: number }>();
    leadsDoTimeFiltrado.forEach((l) => {
      const funil = l.Funil || "Sem Funil";
      const idTime = String(l.IdTime ?? "");
      const key = `${idTime}|${funil}`;
      const atual = porChave.get(key);
      if (atual) atual.total += 1;
      else porChave.set(key, { key, funil, idTime, total: 1 });
    });
    const lista = Array.from(porChave.values());
    const vezesPorNome = new Map<string, number>();
    lista.forEach((f) => vezesPorNome.set(f.funil, (vezesPorNome.get(f.funil) || 0) + 1));
    return lista
      .map((f) => ({
        ...f,
        label: (vezesPorNome.get(f.funil) || 0) > 1 ? `${f.funil} · ${nomeDoTime(f.idTime)}` : f.funil,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [leadsDoTimeFiltrado, times]);

  // Etapas oferecidas no filtro da coluna: as que realmente aparecem na seleção
  // atual (o `stages` do dropdown de mover continua sendo o do time do login).
  const etapasDisponiveis = React.useMemo(() => {
    const nomes = new Set<string>();
    leadsDoTimeFiltrado.forEach((l) => {
      if (funilKey !== "todos" && `${l.IdTime}|${l.Funil || "Sem Funil"}` !== funilKey) return;
      nomes.add(l.Tags || "Sem Etapa");
    });
    return Array.from(nomes).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [leadsDoTimeFiltrado, funilKey]);

  const filteredAndSortedData = React.useMemo(() => {
    return data
      .filter(lead => {
        const matchesTime = filtroTime === "todos" ? true : String(lead.IdTime) === filtroTime;
        // Admin master filtra por (time|funil); os demais perfis seguem pelo nome
        // do funil, como sempre — eles só enxergam o próprio time.
        const matchesFunnel = isAdminMaster
          ? (funilKey === "todos" ? true : `${lead.IdTime}|${lead.Funil || "Sem Funil"}` === funilKey)
          : (selectedFunnel === "Todos" ? true : lead.Funil === selectedFunnel);
        const matchesStages = selectedStages.length === 0 ? true : selectedStages.includes(lead.Tags);
        const matchesSearch = lead.LeadName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             lead.Phone.includes(searchTerm);
        return matchesTime && matchesFunnel && matchesStages && matchesSearch;
      })
      .sort((a, b) => {
          const dateA = a.Data ? new Date(a.Data).getTime() : 0;
          const dateB = b.Data ? new Date(b.Data).getTime() : 0;
          return dateB - dateA;
      });
  }, [data, selectedFunnel, funilKey, filtroTime, isAdminMaster, selectedStages, searchTerm]);

  // Reseta o filtro de etapas ao trocar de funil — etapas anteriores podem não existir no novo funil
  useEffect(() => {
    setSelectedStages([]);
  }, [selectedFunnel, funilKey]);

  // Trocar de time invalida o funil escolhido (ele pertencia ao time anterior).
  useEffect(() => {
    setFunilKey("todos");
    setSelectedStages([]);
  }, [filtroTime]);

  // Filtrar encurta a lista: sem voltar para a página 1, quem estivesse numa
  // página alta cairia numa página vazia.
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroTime, funilKey, selectedFunnel, searchTerm]);

  useEffect(() => {
    // ?? Se estivermos movendo um card, nao rodamos o polling para evitar o "pulo" visual
    if (updating) return; 

    fetchTableData();
    const interval = setInterval(() => {
      fetchTableData();
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedTimeId, selectedUserId, currentPage, pageSize, updating]);

  const fetchTableData = async (): Promise<void> => {
    if (!selectedTimeId || isFetchingTable.current) return;
    // Admin master enxerga TODOS os times do grupo de uma vez. Aguarda a lista de
    // times carregar e manda todos os ids para o backend agregar a leads-list.
    // Demais perfis (admin de time / atendente) seguem com o time próprio.
    if (isAdminMaster && times.length === 0) return;
    const idClienteParam = (isAdminMaster && times.length > 0)
        ? times.map(t => t.id).join(",")
        : selectedTimeId;

    isFetchingTable.current = true;
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/pegarclientes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
              id_cliente: idClienteParam,
              nome_pai: "Todos",
              id_user: localStorage.getItem("id_user"),
              filter_user_id: selectedUserId || null,
              page: currentPage,
              pageSize: pageSize
          }),
      });
      const json = await res.json();
      const leadsList = Array.isArray(json) ? json : (json.leads || []);

      // Se há movimentos pendentes, descarta os dados do servidor
      // para não sobrescrever a atualização otimista
      if (pendingMoves.current > 0) {
        return;
      }

      setData(leadsList.map((item: any) => ({
          key: parseInt(String(item.fone).replace(/\D/g, "")) || Date.now(),
          LeadName: item.nome || "",
          Phone: String(item.fone || ""),
          FullPhone: item.fone_original || String(item.fone || ""),
          Tags: item.nome_funil || "Sem Etapa",
          Funil: item.nome_funil_pai || "Sem Funil",
          Data: item.data_entrada || "",
          Cnpj: item.cnpj || "",
          Razao: item.razao_social || "",
          Obs: item.observacao || "",
          etapaCadencia: item.etapa_cadencia,
          qtdMsgTexto: item.qtd_msg_texto || 0,
          IdUserAtual: item.id_user ?? null,
          IdTime: item.id_time ?? null,
      })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); hasLoadedOnce.current = true; isFetchingTable.current = false; }
  };

  // Espelha fetchTableData num ref p/ o handler do realtime sempre chamar a versão atual.
  useEffect(() => { fetchTableDataRef.current = fetchTableData; });

  // Realtime de leads (funil_fluxo @ banco CRM). UPDATE de etapa/atendente é aplicado
  // LOCALMENTE na hora (mapeia id_funil→nome via stages); INSERT/DELETE (ou UPDATE sem
  // a etapa em mãos) disparam um refetch debounced — o lead completo junta nome do
  // contato/funil que vêm do /pegarclientes. Reusa a agregação existente.
  const agendarRefetchLeads = useCallback(() => {
    if (realtimeLeadsTimer.current) clearTimeout(realtimeLeadsTimer.current);
    realtimeLeadsTimer.current = setTimeout(() => {
      if (pendingMoves.current === 0) fetchTableDataRef.current();
    }, 800);
  }, []);

  const aplicarLeadRealtime = useCallback((payload: any) => {
    const row = payload?.new;
    if (payload?.eventType === "UPDATE" && row && row.fone != null) {
      const stage = stagesRef.current.find((s) => Number(s.id) === Number(row.id_funil));
      if (!stage) { agendarRefetchLeads(); return; } // etapa desconhecida (ex: admin multi-time) → refetch
      const foneN = String(row.fone).replace(/\D/g, "");
      let achou = false;
      setData((prev) => prev.map((l) => {
        const lf = String(l.FullPhone || l.Phone).replace(/\D/g, "");
        if (lf !== foneN) return l;
        achou = true;
        return { ...l, Tags: stage.nome_funil, IdUserAtual: row.id_user ?? l.IdUserAtual, IdTime: row.id_time ?? l.IdTime };
      }));
      if (!achou) agendarRefetchLeads(); // lead ainda não está na tela → refetch
      return;
    }
    // INSERT / DELETE → refetch (precisa juntar nome do contato e do funil)
    agendarRefetchLeads();
  }, [agendarRefetchLeads]);

  // Subscription dos leads (funil_fluxo) filtrada por tenant (policy RLS garante o
  // isolamento). Atendente: o próprio time; admin master: o grupo todo.
  useEffect(() => {
    const supabase = getSupabaseRealtimeCRM();
    if (!supabase) return;
    const id_time = localStorage.getItem("id_cliente");
    const id_grupo = localStorage.getItem("id_grupo");
    const adminMaster = localStorage.getItem("isAdminMaster") === "true";
    const filtro = adminMaster ? `id_cliente=eq.${id_grupo}` : `id_time=eq.${id_time}`;
    const channel = supabase
      .channel(`rt-leads-${id_time || "x"}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "funil_fluxo", filter: filtro },
        (payload) => aplicarLeadRealtime(payload))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [aplicarLeadRealtime]);

  const fetchStages = async () => {
    if (isFetchingStages.current || !selectedTimeId) return;
    isFetchingStages.current = true;
    try {
      const response = await fetch(`${API_URL}/pegaretapaschat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: selectedTimeId }),
      });
      const data = await response.json();
      // Filtra por funil selecionado (se não for "Todos")
      const pai = (selectedFunnel !== "Todos") ? selectedFunnel : null;
      const filtered = pai ? data.filter((s: any) => s.funil === pai) : data;
      setStages(filtered);
    } catch (err) { console.error(err); }
    finally { isFetchingStages.current = false; }
  };

  // ── Etapas por time (Kanban do admin master) ───────────────────────────────
  // `stages` só tem as etapas do time do LOGIN. No Kanban as colunas saem daí e o
  // agrupamento é por nome de etapa, então lead de outro time não encontrava
  // coluna nenhuma e o quadro ficava vazio. Aqui as etapas de cada time são
  // buscadas sob demanda (uma chamada por time, cacheada) para o quadro montar as
  // colunas do time em foco.
  const [etapasPorTime, setEtapasPorTime] = useState<Record<string, Stage[]>>({});
  const etapasPorTimeRef = useRef<Record<string, Stage[]>>({});
  useEffect(() => { etapasPorTimeRef.current = etapasPorTime; }, [etapasPorTime]);
  const buscandoEtapas = useRef<Set<string>>(new Set());

  const fetchEtapasDoTime = useCallback(async (idTime: string) => {
    if (!idTime || etapasPorTimeRef.current[idTime] || buscandoEtapas.current.has(idTime)) return;
    buscandoEtapas.current.add(idTime);
    try {
      const res = await fetch(`${API_URL}/pegaretapaschat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: idTime }),
      });
      const json = await res.json();
      if (Array.isArray(json)) setEtapasPorTime((prev) => ({ ...prev, [idTime]: json }));
    } catch (err) { console.error(err); }
    finally { buscandoEtapas.current.delete(idTime); }
  }, []);

  // Busca só o que o quadro precisa: o time do funil escolhido, ou o time
  // filtrado, ou (em "Todos os times") os times que têm lead em tela.
  useEffect(() => {
    if (viewMode !== "kanban" || !isAdminMaster) return;
    const alvos = new Set<string>();
    if (funilKey !== "todos") alvos.add(funilKey.split("|")[0]);
    else if (filtroTime !== "todos") alvos.add(filtroTime);
    else leadsDoTimeFiltrado.forEach((l) => { if (l.IdTime != null) alvos.add(String(l.IdTime)); });
    alvos.forEach((id) => fetchEtapasDoTime(id));
  }, [viewMode, isAdminMaster, filtroTime, funilKey, leadsDoTimeFiltrado, fetchEtapasDoTime]);

  // Colunas do Kanban. Admin master: saem do time em foco — funil escolhido (a
  // chave carrega o time), senão o time filtrado, senão ("Todos os times") a
  // união das etapas dos times com lead em tela. Etapas que aparecem nos leads
  // mas não vieram do backend viram coluna assim mesmo: nenhum lead pode sumir.
  // Dedup por nome porque o agrupamento do quadro é por nome de etapa — dois
  // funis do mesmo time podem repetir "EM CONTATO" e duplicariam a coluna.
  const stagesKanban = React.useMemo(() => {
    if (!isAdminMaster) return stages;
    const nomesEmTela = Array.from(new Set(filteredAndSortedData.map((l) => l.Tags || "Sem Etapa")));
    let base: Stage[] = [];
    if (funilKey !== "todos") {
      const [idTime, funil] = funilKey.split("|");
      base = (etapasPorTime[idTime] || []).filter((s) => (s.funil || "Sem Funil") === funil);
    } else if (filtroTime !== "todos") {
      base = etapasPorTime[filtroTime] || [];
    } else {
      const porNome = new Map<string, Stage>();
      Object.values(etapasPorTime).forEach((lista) =>
        lista.forEach((s) => { if (!porNome.has(s.nome_funil)) porNome.set(s.nome_funil, s); }));
      base = nomesEmTela.map((nome) => porNome.get(nome)).filter(Boolean) as Stage[];
    }
    const faltantes: Stage[] = nomesEmTela
      .filter((nome) => !base.some((s) => s.nome_funil === nome))
      .map((nome, i) => ({ id: -(i + 1), nome_funil: nome }));
    const porNomeFinal = new Map<string, Stage>();
    [...base, ...faltantes].forEach((s) => { if (!porNomeFinal.has(s.nome_funil)) porNomeFinal.set(s.nome_funil, s); });
    return Array.from(porNomeFinal.values()).sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999));
  }, [isAdminMaster, stages, etapasPorTime, funilKey, filtroTime, filteredAndSortedData]);

  const fetchFunnels = async () => {
    if (isFetchingFunnels.current || !selectedTimeId) return;
    isFetchingFunnels.current = true;
    try {
      const response = await fetch(`${API_URL}/pegarpaginas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: selectedTimeId }),
      });
      const data = await response.json();
      if (Array.isArray(data)) setFunnels(data.map((item: any, index: number) => ({ id: index + 1, nome_funil: item.page })));
    } catch (err) { console.error(err); } 
    finally { isFetchingFunnels.current = false; }
  };

  const fetchGroup = async () => {
    if (!selectedTimeId || isFetchingGroup.current) return;
    isFetchingGroup.current = true;
    const id_grupo = localStorage.getItem("id_grupo");
    if (!id_grupo) return;
    try {
      const response = await fetch(`${API_URL}/pegargrupo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_grupo, id_cliente: selectedTimeId }),
      });
      const data = await response.json();
      setGroupMembers(data);
    } catch (error) { console.error(error); } 
    finally { isFetchingGroup.current = false; }
  };

  const updateFunnelStage = async (phoneFull: string, newStage: string, currentStage: string) => {
    try {
      const res = await fetch(`${API_URL}/atualizaretapa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone: phoneFull, nova_etapa: newStage, idcliente: idTimeDoLead(phoneFull) }),
      });
      if (res.ok) await fetchTableData();
    } catch (err) { console.error(err); }
  };

  const updateFunnelPage = async (phoneFull: string, newPage: string) => {
    try {
      const res = await fetch(`${API_URL}/atualizarpagina`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone: phoneFull, nova_pagina: newPage, idcliente: idTimeDoLead(phoneFull) }),
      });
      if (res.ok) await fetchTableData();
    } catch (err) { console.error(err); }
  };

  // Rótulo do atendente no seletor. Admin master vê atendentes de vários times do grupo,
  // então anexa o time pra distinguir (e o rótulo vira a chave única no dropdown).
  const rotuloAtendente = (u: { id: number; nome: string; id_time?: number }) =>
    (isAdminMaster && u.id_time != null) ? `${u.nome} · ${nomeDoTime(u.id_time)}` : u.nome;

  // Transferência de contato entre usuários (admin only). Pode ser no mesmo time OU
  // para outro time do mesmo grupo: o time destino vem do próprio atendente escolhido
  // (cada usuário carrega seu id_time). Atualiza contatos + funil_fluxo no backend
  // (que remapeia a etapa quando troca de time).
  const transferirUsuario = async (
    lead: LeadData, novoUserId: number, novoUserNome: string, novoIdTime?: number
  ) => {
    const origemTime = String(lead.IdTime ?? selectedTimeId);
    const destinoTime = novoIdTime != null ? String(novoIdTime) : origemTime;
    const trocaTime = destinoTime !== origemTime;
    if (lead.IdUserAtual === novoUserId && !trocaTime) return; // sem mudança
    const nomeAtual =
      usuarios.find((u) => u.id === lead.IdUserAtual)?.nome || "ninguém";
    const alvo = trocaTime ? `${novoUserNome} (${nomeDoTime(novoIdTime)})` : novoUserNome;
    const ok = window.confirm(
      `Transferir "${lead.LeadName || lead.Phone}" de ${nomeAtual} para ${alvo}?`
    );
    if (!ok) return;

    // Atualização otimista local — UX instantânea
    setData((prev) =>
      prev.map((l) =>
        l.Phone === lead.Phone || l.FullPhone === lead.FullPhone
          ? { ...l, IdUserAtual: novoUserId, IdTime: novoIdTime != null ? novoIdTime : l.IdTime }
          : l
      )
    );

    try {
      const res = await fetch(`${API_URL}/transferir_usuario`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_time: origemTime,
          novo_id_time: destinoTime, // time destino (mesmo grupo) — backend remapeia a etapa
          fone: lead.FullPhone || lead.Phone,
          novo_id_user: novoUserId,
          de_id_user: lead.IdUserAtual, // origem: move só a linha deste atendente
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(`Falha ao transferir: ${body.error || `HTTP ${res.status}`}`);
        // Rollback otimista
        await fetchTableData();
        return;
      }
      // Confirma com refresh (pega cod_reg novo etc.)
      await fetchTableData();
    } catch (err: any) {
      console.error("Erro ao transferir usuário:", err);
      alert(`Erro de conexão: ${err?.message || "tente novamente"}`);
      await fetchTableData();
    }
  };

  const columns = [
    {
      title: "LEAD",
      dataIndex: "LeadName",
      render: (text: string, record: LeadData) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ fontWeight: 800, color: "#000000", fontSize: "14px", letterSpacing: "-0.03em" }}>
            <EditableNameCell nome={text} fone={record.Phone} onNomeAtualizado={fetchTableData} />
          </div>
          <div 
            style={{ color: "#007aff", fontSize: "10px", fontWeight: 800, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.05em" }}
            onClick={() => handleOpenCardModal(record)}
          >
            <i className="ti ti-id me-1" /> Ver Detalhes
          </div>
        </div>
      ),
    },
    {
      title: "CONTATO",
      dataIndex: "Phone",
      render: (text: string, record: LeadData) => (
        <div className="d-flex align-items-center gap-2">
            {/* MiniChat desabilitado temporariamente
            <div
                style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", color: "white", cursor: "pointer", boxShadow: "0 2px 6px rgba(34, 197, 94, 0.3)" }}
                onClick={() => openMiniChat(record.Phone, record.LeadName)}
            >
                <i className="ti ti-brand-whatsapp" style={{ fontSize: "14px" }} />
            </div>
            */}
            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>{text}</span>
        </div>
      ),
    },
    {
      title: "ETAPA",
      dataIndex: "Tags",
      // Admin master: as etapas ofertadas saem dos leads em tela, senão o filtro
      // só listaria as etapas do time do login e as dos outros times ficariam
      // inalcançáveis. Demais perfis seguem com as etapas do próprio time.
      filters: (isAdminMaster
        ? etapasDisponiveis
        : stages.map(s => s.nome_funil)).map(nome => ({ text: nome, value: nome })),
      filterMultiple: true,
      filteredValue: selectedStages.length > 0 ? selectedStages : null,
      filterSearch: true,
      onFilter: (value: any, record: LeadData) => record.Tags === value,
      render: (text: string, record: LeadData) => {
        const getIPhoneBadgeStyle = (etapa: string) => {
            const index = stages.findIndex(s => s.nome_funil === etapa);
            const cores = [
                { bg: "rgba(0, 122, 255, 0.08)", text: "#007aff" },
                { bg: "rgba(34, 197, 94, 0.08)", text: "#22c55e" },
                { bg: "rgba(255, 159, 10, 0.08)", text: "#ff9f0a" },
                { bg: "rgba(255, 59, 48, 0.08)", text: "#ff3b30" },
                { bg: "rgba(175, 82, 222, 0.08)", text: "#af52de" },
            ];
            const cor = cores[index % cores.length] || cores[0];
            return { background: cor.bg, color: cor.text, borderRadius: "10px", padding: "4px 12px", fontSize: "11px", fontWeight: 700, border: `1px solid ${cor.text}20`, textTransform: "uppercase" as const };
        };
        return (
          <DropdownSelector
            currentText={text}
            buttonStyle={getIPhoneBadgeStyle(text)}
            items={stages.map((stage) => ({ id: stage.id, label: stage.nome_funil }))}
            onSelect={(newStage) => updateFunnelStage(record.FullPhone || record.Phone, newStage, text)}
          />
        );
      },
    },
    {
        title: "FUNIL",
        dataIndex: "Funil",
        render: (text: string, record: LeadData) => (
          <DropdownSelector
            currentText={text || "Selecionar Funil"}
            buttonStyle={{ background: "#f1f5f9", color: "#475569", borderRadius: "10px", padding: "4px 12px", fontSize: "11px", fontWeight: 700 }}
            items={funnels.map((funnel) => ({ id: funnel.id, label: funnel.nome_funil }))}
            onSelect={(newPage) => updateFunnelPage(record.FullPhone || record.Phone, newPage)}
          />
        ),
      },
    {
      title: "DATA",
      dataIndex: "Data",
      render: (text: string) => (
        <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>
             {(() => {
                if (!text) return "NEW";
                const d = new Date(text);
                d.setDate(d.getDate() + 1);
                return d.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit' });
             })()}
        </span>
      )
    },
    // Coluna TIME: só faz sentido para o admin master, o único que vê leads de
    // mais de um time ao mesmo tempo. Somente leitura — quem move lead entre
    // times é o seletor Funil/Etapa do /chat.
    ...((isAdminMaster && times.length > 1) ? [{
      title: "TIME",
      dataIndex: "IdTime",
      filters: times.map(t => ({ text: t.nome, value: String(t.id) })),
      filterMultiple: true,
      filteredValue: filtroTime === "todos" ? null : [filtroTime],
      onFilter: (value: any, record: LeadData) => String(record.IdTime) === String(value),
      render: (_: any, record: LeadData) => (
        <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", background: "rgba(148,163,184,0.12)", borderRadius: "10px", padding: "4px 12px", whiteSpace: "nowrap" }}>
          {record.IdTime != null ? nomeDoTime(record.IdTime) : "—"}
        </span>
      ),
    }] : []),
    // Coluna ATENDENTE: visível só para admin/admin master. Permite transferir
    // o lead entre usuários do mesmo time (UPDATE id_user + cod_reg).
    ...((isAdmin || isAdminMaster) ? [{
      title: "ATENDENTE",
      dataIndex: "IdUserAtual",
      render: (_: any, record: LeadData) => {
        const atual = usuarios.find((u) => u.id === record.IdUserAtual);
        const label = atual?.nome || "Sem atendente";
        return (
          <DropdownSelector
            currentText={label}
            buttonStyle={{
              background: atual ? "rgba(34, 197, 94, 0.08)" : "rgba(148, 163, 184, 0.12)",
              color: atual ? "#16a34a" : "#64748b",
              borderRadius: "10px",
              padding: "4px 12px",
              fontSize: "11px",
              fontWeight: 700,
            }}
            items={usuarios.map((u) => ({ id: u.id, label: rotuloAtendente(u) }))}
            onSelect={(lbl) => {
              const u = usuarios.find((x) => rotuloAtendente(x) === lbl);
              if (u) transferirUsuario(record, u.id, u.nome, u.id_time);
            }}
          />
        );
      },
    }] : []),
  ];

  // ───────────────────────── Vista MOBILE (cards + detalhe) ─────────────────────────
  // Reusa os mesmos estados/handlers do desktop (etapa, funil, atendente, obs, card).
  const formatLeadDate = (text: string) => {
    if (!text) return "NEW";
    const d = new Date(text);
    if (isNaN(d.getTime())) return "NEW";
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };
  const etapaBadgeStyle = (etapa: string): React.CSSProperties => {
    const index = stages.findIndex((s) => s.nome_funil === etapa);
    const cores = [
      { bg: "rgba(0, 122, 255, 0.08)", text: "#007aff" },
      { bg: "rgba(34, 197, 94, 0.08)", text: "#22c55e" },
      { bg: "rgba(255, 159, 10, 0.08)", text: "#ff9f0a" },
      { bg: "rgba(255, 59, 48, 0.08)", text: "#ff3b30" },
      { bg: "rgba(175, 82, 222, 0.08)", text: "#af52de" },
    ];
    const cor = cores[index % cores.length] || cores[0];
    return { background: cor.bg, color: cor.text, borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 700, border: `1px solid ${cor.text}20`, textTransform: "uppercase", whiteSpace: "nowrap" };
  };
  const mLbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94a3b8", display: "block", marginBottom: 4 };
  const mSel: React.CSSProperties = { borderRadius: 10, fontSize: 13, border: isDark ? "1px solid #2F343C" : "1px solid rgba(0,0,0,0.1)" };
  const mRow: React.CSSProperties = { borderTop: "1px solid #f1f5f9" };
  const mK: React.CSSProperties = { fontSize: 12, color: "#94a3b8", fontWeight: 600 };
  const mV: React.CSSProperties = { fontSize: 13, color: isDark ? "#e0e0e0" : "#1e293b", fontWeight: 600 };
  const openMobileDetail = (lead: LeadData) => { setMobileObsDraft(lead.Obs || ""); setMobileLeadDetail(lead); };

  const mobileLeadsView = mobileLeadDetail ? (
    <div style={{ padding: "8px 6px 24px" }}>
      <button onClick={() => setMobileLeadDetail(null)} className="btn btn-sm btn-light d-flex align-items-center gap-1 mb-3" style={{ borderRadius: 10, fontWeight: 600 }}>
        <i className="ti ti-arrow-left" /> Voltar
      </button>
      <div className="card" style={{ borderRadius: 16, border: "1px solid rgba(0,0,0,0.06)" }}>
        <div className="p-3">
          <h5 className="fw-bold mb-1" style={{ color: isDark ? "#e0e0e0" : "#0f172a" }}>{mobileLeadDetail.LeadName || "(sem nome)"}</h5>
          <div className="text-muted mb-3" style={{ fontSize: 13 }}>{mobileLeadDetail.Phone}</div>

          <label style={mLbl}>Etapa</label>
          <select className="form-select mb-3" value={mobileLeadDetail.Tags} style={mSel}
            onChange={(e) => { const v = e.target.value; updateFunnelStage(mobileLeadDetail.FullPhone || mobileLeadDetail.Phone, v, mobileLeadDetail.Tags); setMobileLeadDetail({ ...mobileLeadDetail, Tags: v }); }}>
            {stages.map((s) => <option key={s.id} value={s.nome_funil}>{s.nome_funil}</option>)}
          </select>

          <label style={mLbl}>Funil</label>
          <select className="form-select mb-3" value={mobileLeadDetail.Funil} style={mSel}
            onChange={(e) => { const v = e.target.value; updateFunnelPage(mobileLeadDetail.FullPhone || mobileLeadDetail.Phone, v); setMobileLeadDetail({ ...mobileLeadDetail, Funil: v }); }}>
            {funnels.map((f) => <option key={f.id} value={f.nome_funil}>{f.nome_funil}</option>)}
          </select>

          {(isAdmin || isAdminMaster) && (
            <>
              <label style={mLbl}>Atendente</label>
              <select className="form-select mb-3" value={mobileLeadDetail.IdUserAtual ?? ""}
                style={{ ...mSel, color: mobileLeadDetail.IdUserAtual ? "#16a34a" : "#64748b", fontWeight: 700 }}
                onChange={(e) => { const id = parseInt(e.target.value, 10); const u = usuarios.find((x) => x.id === id); if (u) { transferirUsuario(mobileLeadDetail, u.id, u.nome, u.id_time); setMobileLeadDetail({ ...mobileLeadDetail, IdUserAtual: u.id, IdTime: u.id_time ?? mobileLeadDetail.IdTime }); } }}>
                <option value="" disabled>Sem atendente</option>
                {usuarios.map((u) => <option key={u.id} value={u.id}>{rotuloAtendente(u)}</option>)}
              </select>
            </>
          )}

          <label style={mLbl}>Observação</label>
          <textarea className="form-control mb-2" rows={4} value={mobileObsDraft} placeholder="Sem observação"
            style={{ ...mSel, height: "auto" }} onChange={(e) => setMobileObsDraft(e.target.value)} />
          <button disabled={mobileObsSaving} className="btn btn-sm btn-primary mb-3" style={{ borderRadius: 10, fontWeight: 600 }}
            onClick={async () => {
              setMobileObsSaving(true);
              try {
                await fetch(`${API_URL}/marcarobs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fone: mobileLeadDetail.FullPhone || mobileLeadDetail.Phone, observacao: mobileObsDraft, id_cliente: localStorage.getItem("id_cliente") }) });
                setData((prev) => prev.map((l) => l.key === mobileLeadDetail.key ? { ...l, Obs: mobileObsDraft } : l));
                setMobileLeadDetail({ ...mobileLeadDetail, Obs: mobileObsDraft });
              } catch { /* noop */ } finally { setMobileObsSaving(false); }
            }}>
            {mobileObsSaving ? "Salvando..." : "Salvar observação"}
          </button>

          <div className="d-flex justify-content-between py-2" style={mRow}><span style={mK}>Data de entrada</span><span style={mV}>{formatLeadDate(mobileLeadDetail.Data)}</span></div>
          {mobileLeadDetail.Cnpj && <div className="d-flex justify-content-between py-2" style={mRow}><span style={mK}>CNPJ</span><span style={mV}>{mobileLeadDetail.Cnpj}</span></div>}
          {mobileLeadDetail.Razao && <div className="d-flex justify-content-between py-2 gap-2" style={mRow}><span style={mK}>Razão social</span><span style={{ ...mV, textAlign: "right" }}>{mobileLeadDetail.Razao}</span></div>}

          <button onClick={() => handleOpenCardModal(mobileLeadDetail)} className="btn btn-outline-primary w-100 mt-3" style={{ borderRadius: 10, fontWeight: 700 }}>
            <i className="ti ti-id me-1" /> Ver Card completo
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div style={{ padding: "4px 6px 24px" }}>
      {filteredAndSortedData.map((lead: LeadData) => {
        const atual = usuarios.find((u) => u.id === lead.IdUserAtual);
        return (
          <div key={lead.key} onClick={() => openMobileDetail(lead)} className="card mb-2" style={{ borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <h6 className="fw-bold mb-0" style={{ color: isDark ? "#e0e0e0" : "#0f172a", fontSize: 15 }}>{lead.LeadName || "(sem nome)"}</h6>
                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, whiteSpace: "nowrap" }}>{formatLeadDate(lead.Data)}</span>
              </div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{lead.Phone}</div>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {lead.Tags && <span style={etapaBadgeStyle(lead.Tags)}>{lead.Tags}</span>}
                {lead.Funil && <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 8, padding: "3px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{lead.Funil}</span>}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: atual ? "#16a34a" : "#94a3b8" }}>
                  <i className="ti ti-user" style={{ fontSize: 13 }} /> {atual?.nome || "Sem atendente"}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#007aff", display: "inline-flex", alignItems: "center", gap: 2 }}>
                  Ver Detalhes <i className="ti ti-chevron-right" style={{ fontSize: 14 }} />
                </span>
              </div>
            </div>
          </div>
        );
      })}
      {filteredAndSortedData.length === 0 && <div className="text-center py-5 text-muted">Nenhum lead encontrado.</div>}
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .page-wrapper, .kanban-wrapper, .iphone-table-wrapper { font-family: 'Inter', sans-serif !important; }
        .page-wrapper :not(.ti):not(.fa):not(.fa-brands):not(.fa-solid), .kanban-wrapper :not(.ti):not(.fa):not(.fa-brands):not(.fa-solid) { font-family: 'Inter', sans-serif; }
        .ant-table { background: transparent !important; }
        .ant-table-thead > tr > th { background: rgba(241, 245, 249, 0.5) !important; color: #475569 !important; font-weight: 700 !important; font-size: 11px !important; text-transform: uppercase !important; letter-spacing: 0.05em !important; border-bottom: 1px solid #e2e8f0 !important; padding: 16px !important; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #f1f5f9 !important; padding: 14px 16px !important; color: #1e293b !important; font-size: 13.5px !important; line-height: 1.5 !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(59, 130, 246, 0.04) !important; }

        /* Dark mode overrides */
        [data-theme="dark"] .ant-table-thead > tr > th { background: #1F2937 !important; color: #D8DFEE !important; border-bottom: 1px solid #2F343C !important; }
        [data-theme="dark"] .ant-table-tbody > tr > td { border-bottom: 1px solid #2F343C !important; color: #C8D2E0 !important; }
        [data-theme="dark"] .ant-table-tbody > tr:hover > td { background: rgba(59, 130, 246, 0.08) !important; }

        /* Esconder buscas nativas e labels redundantes */
        .iphone-table-wrapper input[type="search"],
        .iphone-table-wrapper .ant-input-search,
        .iphone-table-wrapper .dataTables_filter {
          display: none !important;
        }

        /* Filtro multi-select no header da coluna ETAPA — dropdown nativo Ant Design */
        .ant-table-filter-trigger {
          color: #94a3b8 !important;
        }
        .ant-table-filter-trigger.active {
          color: #007aff !important;
        }
        .ant-table-filter-dropdown {
          background: #ffffff !important;
          border-radius: 12px !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
          border: 1px solid #e2e8f0 !important;
          padding: 4px 0 !important;
          min-width: 220px !important;
        }
        [data-theme="dark"] .ant-table-filter-dropdown {
          background: #1F2937 !important;
          border-color: #2F343C !important;
        }
        [data-theme="dark"] .ant-table-filter-dropdown .ant-dropdown-menu-item,
        [data-theme="dark"] .ant-table-filter-dropdown .ant-checkbox-wrapper {
          color: #D8DFEE !important;
        }

        /* ── Mobile: header da leads-list limpo ── */
        @media (max-width: 767px) {
          .leads-main-card { border-radius: 16px !important; }
          .leads-page-wrapper .content { padding: 8px !important; }
          /* header empilha: funil em cima (largura total), busca embaixo */
          .leads-main-card > .card-header {
            padding: 10px 12px 0 12px !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
          }
          /* min-width:0 em toda a cadeia: sem isso os itens flex expandem até o
             conteúdo (689px) e vazam o card (overflow:hidden corta os funis).
             Com min-width:0 o wrapper encolhe à largura do card e o seu
             overflow-x:auto passa a rolar por toque, revelando todos os funis. */
          .leads-main-card > .card-header > div:first-child { margin-right: 0 !important; min-width: 0 !important; width: 100% !important; }
          .tabs-navigation-container { max-width: 100% !important; width: 100% !important; min-width: 0 !important; }
          /* chevrons de scroll sobrepunham o seletor — fora no mobile (rola por toque) */
          .leads-scroll-btn { display: none !important; }
          .funnel-tabs-wrapper {
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            touch-action: pan-x;
            padding: 4px 8px !important;
          }
          /* toggle lista/kanban escondido (mobile usa os cards) */
          .leads-view-toggle { display: none !important; }
          /* busca ocupa a linha toda */
          .leads-main-card > .card-header > div:last-child { width: 100%; }
          .leads-main-card > .card-header > div:last-child > div:first-child { width: 100% !important; }
          .iphone-table-wrapper { margin-top: 4px !important; padding: 0 6px !important; }
        }
      `}</style>

      <div className="page-wrapper leads-page-wrapper" style={{ minHeight: "100vh" }}>
        <div className="content">
          <div className="card leads-main-card" style={{ borderRadius: "32px", overflow: "hidden" }}>
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3" style={{ background: "transparent", borderBottom: "none", padding: "8px 24px 0 24px" }}>
            <div className="d-flex align-items-center gap-1" style={{ flex: 1, minWidth: 0, marginRight: '40px' }}>
              {/* Seletor de TIME — só o admin master enxerga mais de um time.
                  Filtra em tela (os leads de todos os times já vieram juntos) e
                  redefine quais funis aparecem nas abas. */}
              {isAdminMaster && times.length > 1 && (
                <select
                  className="leads-time-filter"
                  value={filtroTime}
                  onChange={(e) => setFiltroTime(e.target.value)}
                  title="Filtrar por time"
                  style={{
                    marginRight: '10px', padding: '8px 12px', borderRadius: '12px', fontSize: '13px',
                    fontWeight: 700, cursor: 'pointer', maxWidth: '210px',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                    background: isDark ? '#1F2937' : '#ffffff',
                    color: filtroTime === "todos" ? (isDark ? '#9CA3AF' : '#64748b') : '#007aff',
                  }}
                >
                  <option value="todos">Todos os times ({data.length})</option>
                  {times.map((t) => {
                    const n = data.filter((l) => String(l.IdTime) === String(t.id)).length;
                    return <option key={t.id} value={String(t.id)}>{t.nome} ({n})</option>;
                  })}
                </select>
              )}
              {/* min-width:0 (aqui e no wrapper abaixo) é obrigatório: flex item
                  nasce com min-width:auto e não encolhe abaixo do min-content —
                  com muitos funis o wrapper esticava até o conteúdo e passava por
                  baixo da busca. Com min-width:0 ele para na largura disponível e
                  o overflow-x rola (chevrons). maxWidth em 100vw não servia: 100vw
                  inclui a sidebar, então o teto ficava maior que o espaço real. */}
              <div className="tabs-navigation-container" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, maxWidth: '100%' }}>
                <button onClick={() => scrollTabs('left')} className="leads-scroll-btn" style={{ position: 'absolute', left: '-10px', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 20, cursor: 'pointer' }}>
                  <i className="ti ti-chevron-left" style={{ fontSize: '16px', color: '#007aff', fontWeight: 'bold' }} />
                </button>
                <div className="funnel-tabs-wrapper" ref={tabsRef} style={{ display: 'flex', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', padding: '4px 40px', borderRadius: '16px', gap: '4px', overflowX: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none', scrollBehavior: 'smooth', width: '100%', minWidth: 0 }}>
                  <style>{`.funnel-tabs-wrapper::-webkit-scrollbar { display: none; }`}</style>
                  {(() => {
                    // Aba "Todos": no modo admin master zera a chave (time|funil).
                    const ativa = isAdminMaster ? funilKey === "todos" : selectedFunnel === "Todos";
                    return (
                  <button onClick={() => { setSelectedFunnel("Todos"); setFunilKey("todos"); setNomePai("Leads"); }} style={{ padding: '8px 20px', borderRadius: '12px', border: 'none', fontSize: '13px', fontWeight: 700, transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', background: ativa ? (isDark ? '#1F2937' : '#ffffff') : 'transparent', color: ativa ? '#007aff' : (isDark ? '#9CA3AF' : '#64748b'), boxShadow: ativa ? (isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)') : 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                    <i className="ti ti-layout-grid" style={{ fontSize: '14px', opacity: ativa ? 1 : 0.5 }} /> Todos
                    {ativa && <div style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', background: '#007aff', borderRadius: '50%' }} />}
                  </button>
                    );
                  })()}
                  {/* Admin master: abas vindas dos leads em tela (funis de todos os
                      times do grupo). Demais perfis: os funis do próprio time. */}
                  {isAdminMaster ? funisDisponiveis.map(f => {
                    const ativa = funilKey === f.key;
                    return (
                      <button key={f.key} onClick={() => { setFunilKey(f.key); setSelectedFunnel(f.funil); setNomePai(f.funil); localStorage.setItem("nome_pai", f.funil); }} style={{ padding: '8px 20px', borderRadius: '12px', border: 'none', fontSize: '13px', fontWeight: 700, transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', background: ativa ? (isDark ? '#1F2937' : '#ffffff') : 'transparent', color: ativa ? '#007aff' : (isDark ? '#9CA3AF' : '#64748b'), boxShadow: ativa ? (isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)') : 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                        <i className="ti ti-filter" style={{ fontSize: '14px', opacity: ativa ? 1 : 0.5 }} /> {f.label}
                        <span style={{ fontSize: '10px', padding: '2px 6px', background: ativa ? 'rgba(0,122,255,0.1)' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'), borderRadius: '6px', opacity: 0.8 }}>{f.total}</span>
                        {ativa && <div style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', background: '#007aff', borderRadius: '50%' }} />}
                      </button>
                    );
                  }) : funnels.map(f => (
                    <button key={f.id} onClick={() => { setSelectedFunnel(f.nome_funil); setNomePai(f.nome_funil); localStorage.setItem("nome_pai", f.nome_funil); }} style={{ padding: '8px 20px', borderRadius: '12px', border: 'none', fontSize: '13px', fontWeight: 700, transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', background: selectedFunnel === f.nome_funil ? (isDark ? '#1F2937' : '#ffffff') : 'transparent', color: selectedFunnel === f.nome_funil ? '#007aff' : (isDark ? '#9CA3AF' : '#64748b'), boxShadow: selectedFunnel === f.nome_funil ? (isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)') : 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                      <i className="ti ti-filter" style={{ fontSize: '14px', opacity: selectedFunnel === f.nome_funil ? 1 : 0.5 }} /> {f.nome_funil}
                      <span style={{ fontSize: '10px', padding: '2px 6px', background: selectedFunnel === f.nome_funil ? 'rgba(0,122,255,0.1)' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'), borderRadius: '6px', opacity: 0.8 }}>{data.filter(l => l.Funil === f.nome_funil).length}</span>
                      {selectedFunnel === f.nome_funil && <div style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', background: '#007aff', borderRadius: '50%' }} />}
                    </button>
                  ))}
                </div>
                <button onClick={() => scrollTabs('right')} className="leads-scroll-btn" style={{ position: 'absolute', right: '-10px', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 20, cursor: 'pointer' }}>
                  <i className="ti ti-chevron-right" style={{ fontSize: '16px', color: '#007aff', fontWeight: 'bold' }} />
                </button>
              </div>
            </div>
            
            {/* flexShrink:0 — a busca não cede espaço; quem encolhe (e rola) é a
                faixa de funis. Sem isso a expansão do input no focus disputava
                largura com as abas. */}
            <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
              {/* 🆕 Busca iPhone 17 Pill (Compacta e Integrada) */}
              <div style={{ position: 'relative', width: window.innerWidth < 768 ? '140px' : '180px', transition: 'all 0.3s ease' }}>
                <i className="ti ti-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px', pointerEvents: 'none' }} />
                <input 
                  type="text"
                  placeholder="Pesquisar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 35px',
                    borderRadius: '50px',
                    border: isDark ? '1px solid #2F343C' : '1px solid rgba(0,0,0,0.05)',
                    background: isDark ? '#1F2937' : 'rgba(255,255,255,0.8)',
                    backdropFilter: isDark ? 'none' : 'blur(5px)',
                    color: isDark ? '#D8DFEE' : undefined,
                    fontSize: '13px',
                    fontWeight: 500,
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.parentElement!.style.width = '280px';
                    e.currentTarget.style.borderColor = '#007aff';
                    e.currentTarget.style.background = isDark ? '#1F2937' : '#ffffff';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,122,255,0.08)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.parentElement!.style.width = '180px';
                    e.currentTarget.style.borderColor = isDark ? '#2F343C' : 'rgba(0,0,0,0.05)';
                    e.currentTarget.style.background = isDark ? '#1F2937' : 'rgba(255,255,255,0.8)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.02)';
                  }}
                />
              </div>

              <div className="d-flex align-items-center border rounded p-1 icon-list leads-view-toggle">
                <button onClick={() => setViewMode("list")} className={`btn btn-icon btn-sm ${viewMode === "list" ? "active bg-primary text-white" : ""}`}><i className="ti ti-list-tree" /></button>
                <button onClick={() => setViewMode("kanban") } className={`btn btn-icon btn-sm ${viewMode === "kanban" ? "active bg-primary text-white" : ""}`}><i className="ti ti-layout-grid" /></button>
              </div>
            </div>
          </div>
            <div className="card-body p-0" style={{ 
                maxHeight: "calc(100vh - 150px)", 
                overflowY: "auto" 
            }}>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status"><span className="visually-hidden">Carregando...</span></div>
                  <h6 className="text-muted">Carregando leads...</h6>
                </div>
              ) : empty ? (
                <div className="text-center py-5">
                  <i className="ti ti-inbox-off" style={{ fontSize: "48px", color: "#cbd5e1" }} />
                  <h5 className="mt-3" style={{ color: "#64748b" }}>Nenhum lead encontrado</h5>
                  <p className="text-muted">Os leads aparecerão aqui quando forem adicionados.</p>
                </div>
              ) : (
                viewMode === "list" ? (
                  <div className="iphone-table-wrapper" style={{ padding: "0 10px", marginTop: "20px" }}>
                    <ResponsiveLeadsList desktopComponent={<Table dataSource={filteredAndSortedData} columns={columns} Selection={true} pagination={{ current: currentPage, pageSize: pageSize, total: filteredAndSortedData.length, showSizeChanger: false }} onChange={handleTableChange} />} mobileLeads={filteredAndSortedData} mobileComponent={mobileLeadsView} />
                  </div>
                ) : (
                  <div style={{ marginTop: "30px" }}>
                    <KanbanDisplay stages={stagesKanban} leads={filteredAndSortedData} onMoveLead={handleMoveLead} onOpenCard={handleOpenCardModal} onOpenChat={openMiniChat} />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      <CrmsModal />
      <CardViewModal show={cardModalShow} onHide={() => setCardModalShow(false)} leadPhone={selectedLeadPhone} leadName={selectedLeadName} initialFunnelName={selectedFunnelName} />
      <Modal show={showChatModal} onHide={() => setShowChatModal(false)} size="lg" centered>
        <Modal.Body className="p-0">
          {selectedChatContact && <MiniChat phone={selectedChatContact.phone} name={selectedChatContact.name} onClose={() => setShowChatModal(false)} />}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LeadsList;